import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { FaSpinner, FaUserTie, FaClock, FaGraduationCap, FaStar } from 'react-icons/fa';

const CourseDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [course, setCourse] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchCourse = async () => {
            try {
                const { data } = await api.get(`/courses/${id}`);
                setCourse(data);
            } catch (err) {
                setError('Failed to load course details. Please try again.');
            } finally {
                setLoading(false);
            }
        };
        fetchCourse();
    }, [id]);

    if (loading) {
        return (
            <div className="container py-5 text-center">
                <FaSpinner className="fa-spin fa-3x text-primary mb-3" />
                <h4>Loading Course...</h4>
            </div>
        );
    }

    if (error) {
        return <div className="container py-5 text-center"><h4 className="text-danger">{error}</h4></div>;
    }

    if (!course) {
        return <div className="container py-5 text-center"><h4>Course not found.</h4></div>;
    }

    const formatPrice = (price) => {
        if (typeof price !== 'number') return 'N/A';
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES',
            minimumFractionDigits: 0,
        }).format(price);
    };

    return (
        <div className="container-xxl py-5">
            <div className="container">
                <div className="row g-5">
                    <div className="col-lg-8 wow fadeInUp" data-wow-delay="0.1s">
                        <img className="img-fluid rounded-3 mb-4" src={course.thumbnail || 'https://via.placeholder.com/800x450?text=Course+Image'} alt={course.title} />
                        <h1 className="mb-3 display-6 fw-bold">{course.title}</h1>
                        <div className="d-flex align-items-center mb-4">
                            <span className="badge bg-primary me-3 p-2 fs-6">{course.category}</span>
                            <div className="text-muted">
                                {[...Array(5)].map((_, i) => (
                                    <FaStar key={i} className={`text-${i < Math.floor(course.rating || 0) ? 'primary' : 'light'}`} />
                                ))}
                                <span className="ms-2">({course.rating || 0} reviews)</span>
                            </div>
                        </div>
                        <p className="mb-4">{course.description}</p>
                        
                        <h3 className="mb-3">What you'll learn</h3>
                        <ul className="list-unstyled">
                            {course.lessons && course.lessons.slice(0, 5).map(lesson => (
                                <li key={lesson._id} className="mb-2"><i className="fa fa-check text-primary me-2"></i>{lesson.title}</li>
                            ))}
                             {course.lessons && course.lessons.length > 5 && (
                                <li className="mb-2"><i className="fa fa-check text-primary me-2"></i>And {course.lessons.length - 5} more lessons...</li>
                            )}
                        </ul>
                    </div>
                    <div className="col-lg-4 wow fadeInUp" data-wow-delay="0.3s">
                        <div className="card position-sticky" style={{ top: '6rem' }}>
                            <div className="card-body">
                                <h3 className="card-title fw-bold">{formatPrice(course.price)}</h3>
                                <button 
                                    className="btn btn-primary w-100 my-3"
                                    onClick={() => navigate(`/courses/${course._id}/enroll`)}
                                >
                                    Enroll Now
                                </button>
                                <ul className="list-group list-group-flush">
                                    <li className="list-group-item d-flex justify-content-between">
                                        <span><FaUserTie className="me-2 text-primary"/> Instructor</span>
                                        <strong>{course.instructor?.name || 'N/A'}</strong>
                                    </li>
                                    <li className="list-group-item d-flex justify-content-between">
                                        <span><FaGraduationCap className="me-2 text-primary"/> Level</span>
                                        <strong>{course.level}</strong>
                                    </li>
                                    <li className="list-group-item d-flex justify-content-between">
                                        <span><FaClock className="me-2 text-primary"/> Duration</span>
                                        <strong>{course.duration}</strong>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CourseDetail;
